<template>
  <BaseAutoCompleteWithSingleSelectedItem
    v-model="selectedItem"
    :label="i18n.global.t('pages.vehicles.form.firmwareSettingCopy')"
    itemName="name"
    :items="items"
    :operation-offices="
      operationOffice === undefined ? undefined : [operationOffice]
    "
    :error-messages="errorMessages"
    :disabled="disabled"
  />
</template>
<script setup lang="ts">
import BaseAutoCompleteWithSingleSelectedItem from '@/components/AutoCompletes/BaseAutoCompleteWithSingleSelectedItem.vue';
import Api from '@/services/api/ApiServiceFabrick';
import getOperationOffice from '@/types/responses/getOperationOffice';
import getVehicleResponse from '@/types/responses/getVehicle';
import { onMounted, ref, watch } from 'vue';
import i18n from '@/lang/i18n';

const props = withDefaults(
  defineProps<{
    modelValue: ItemType | undefined;
    initialItemId?: number;
    operationOffice?: getOperationOffice | undefined;
    errorMessages?: string[];
    readonly?: boolean;
    disabled?: boolean;
  }>(),
  {
    readonly: false,
    disabled: false,
  }
)

const emit = defineEmits(['update:modelValue']);

type ItemType = {
  id: number;
  name: string;
  operationOfficeId: number;
  vehicle: getVehicleResponse;
};

const selectedItem = ref<ItemType | undefined>(undefined);
const items = ref<ItemType[]>([]);

onMounted(async () => {
  await getItems();
  selectedItem.value = items.value.find(
    (item) => item.id === props.initialItemId
  );
});

// operationOfficeが変更された時にitemsを更新する
watch(
  () => props.operationOffice,
  () => {
    getItems();
  }
);

// initialItemIdが変更された時にselectedItemを更新する
watch(
  () => props.initialItemId,
  () => {
    selectedItem.value = items.value.find(
      (item) => item.id === props.initialItemId
    );
  }
);

// selectedItemがitemsに含まれていない場合は選択を解除する
watch(
  () => items.value,
  () => {
    selectedItem.value = items.value.find(
      (item) => item.id === selectedItem.value?.id
    );
  }
);

// 変更を親コンポーネントに伝える
watch(
  () => selectedItem.value,
  (newValue) => {
    emit('update:modelValue', newValue);
  }
);

const getItems = async () => {
  if (!props.operationOffice) {
    items.value = [];
    return;
  }

  const params = {
    status: 'all',
    operation_office_id: props.operationOffice.id,
  };

  const [devices, vehicles] = await Promise.all([
    Api().autoComplete.devices(params, true),
    Api().autoComplete.vehicles(params, true),
  ]);

  const itemsTmp: ItemType[] = [];

  devices.forEach((device) => {
    const vehicle = vehicles.find(
      (vehicle) => vehicle.digital_tacograph === device.id
    );

    if (vehicle === undefined || device.status) {
      return;
    }

    itemsTmp.push( {
      id: device.id,
      name: device.device_id,
      operationOfficeId: device.operation_office_id,
      vehicle,
    });
  });

  items.value = itemsTmp;
};
</script>
